/* global CriteriaHelper */
import * as React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { arrayMove } from '@dnd-kit/sortable';

// Components
import { ConfirmationModal } from '../../../../../../../../components/ConfirmationModal';
import Table from '../../../../../../../../components/Table';
import { Button } from '../../../../../../../../components/Button';
import { PredicatePartEditor } from './PredicatePartEditor';

// Conf
import { uiConfig } from '../../../../../../../conf/predicateParts';

// Enums
import { dtoState } from '../../../../../../../../common/enums/dtoState';

// Hooks
import { usePredicateStorePredicates } from '../../../../../../state/predicate/hooks/usePredicateStorePredicates';

// Util
import { translateUIConfigForTable, translateUIConfigForAddEdit } from '../../../../../../../../common/utility';

const TableControlHeader = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    background: #fafafa;
    display: flex;
    flex-direction: row;
    padding: 0.5rem;
    justify-content: space-between;
    border-top: 2px solid #eeeeee;
    border-bottom: 2px solid #eeeeee;
    align-items: center;
    margin-top: -14px;
    z-index: 1;
`;
const TableHeader = styled.h4`
    font-weight: 600;
    color: #333333;
    font-size: 0.9rem;
`;

const getInitialTempSortOrder = ({ predicateParts = [] }) => {
    return predicateParts.sort((a, b) => a.predicatePartOrder - b.preticatePartOrder).map(({ uuid }) => uuid);
};

export const PredicatePartsTable = ({
    parentPredicateUuid,
    predicatePartUuids,
    onApply,
    onDelete,
    onChangeSortOrder,
    setEdited,
    parentDrawerRef
}) => {
    const [tempSortOrder, setTempSortOrder] = React.useState(null);
    const predicateParts = usePredicateStorePredicates(predicatePartUuids);
    const [activeId, setActiveId] = React.useState();
    const [editing, setEditing] = React.useState(null);
    const [deleting, setDeleting] = React.useState(null);
    const [fatalError, setFatalError] = React.useState(null);
    const { columns, actions, initialState } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);

    React.useEffect(() => {
        if (predicateParts?.length > 0 && (tempSortOrder === null || tempSortOrder.length < predicateParts.length)) {
            setTempSortOrder(getInitialTempSortOrder({ predicateParts }));
        }
    }, [setTempSortOrder, predicateParts, tempSortOrder]);

    const tableData = React.useMemo(() => {
        if (!predicateParts || !predicateParts.length || tempSortOrder === null) return [];
        const hydratedPredicateParts = predicateParts
            .filter((part) => part.dtoState !== dtoState.Deleted)
            .map((v) => ({
                ...v,
                DataTableActions: actions.map((i) => ({
                    ...i,
                    action: () => i.action(v[uiConfig.primaryKey])
                    // buttonProps: {
                    //     disabled: v.dtoState === dtoState.Deleted
                    // }
                }))
            }));

        return tempSortOrder.map((v) => hydratedPredicateParts.find(({ uuid }) => v === uuid)).filter(Boolean);
    }, [predicateParts, actions, tempSortOrder]);

    const closeEditor = () => {
        setEditing(null);
        if (parentDrawerRef) {
            parentDrawerRef?.current?.drawerFocus?.();
        }
    };

    const isExistingDoc = (doc) => {
        return false;
        // return (
        //     (data ?? []).find((v) => {
        //         return v.codeId !== doc.codeId && v.code === doc.code && v.displayName === doc.displayName;
        //     }) !== undefined
        // );
    };

    const deletingPart = React.useMemo(() => {
        if (!deleting) return null;
        return (predicateParts ?? []).find(({ uuid }) => uuid === deleting);
    }, [deleting, predicateParts]);

    function handleDragStart(event) {
        setActiveId(event.active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active && over && active?.id !== over?.id) {
            const oldIndex = tempSortOrder.indexOf(active.id);
            const newIndex = tempSortOrder.indexOf(over.id);
            const sorted = arrayMove([...tempSortOrder], oldIndex, newIndex);

            setTempSortOrder(sorted);
            onChangeSortOrder(sorted);
            setEdited(true);
        }

        setActiveId(null);
    }

    function handleDragCancel() {
        setActiveId(null);
    }

    return (
        <>
            <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <TableControlHeader>
                    <TableHeader />
                    <Button
                        key={'add-new-part'}
                        tabIndex="0"
                        appearance="primary-dark"
                        onClick={() => setEditing('new')}
                    >
                        Add New Part
                    </Button>
                </TableControlHeader>
                <Table
                    withHeader={false}
                    columns={columns}
                    data={tableData}
                    withActions={actions !== null}
                    sortableRows={actions.some(({ type }) => type === 'drag')}
                    id={'predicate-part-table'}
                    label={'Criteria Predicate Part Entries'}
                    showControlDeck={false}
                    paginated={false}
                    initialState={initialState}
                    dndContextProps={{
                        onDragEnd: handleDragEnd,
                        onDragStart: handleDragStart,
                        onDragCancel: handleDragCancel,
                        getDraggableId: (rowData = {}) => rowData?.uuid,
                        activeId
                    }}
                    tableProps={{
                        style: { borderCollapse: 'separate', minHeight: '0' }
                    }}
                    emptyStateProps={{
                        customLabel: 'No Predicate Parts.',
                        labelFontSize: 500,
                        imageSize: 3.5
                    }}
                />
            </div>
            {editing !== null && (
                <PredicatePartEditor
                    isOpen={editing !== null}
                    parentPredicateUuid={parentPredicateUuid}
                    formModel={translateUIConfigForAddEdit(uiConfig)}
                    saveDisabled={false}
                    selectedPredicatePartUuid={editing}
                    closeDrawer={closeEditor}
                    isExistingDoc={isExistingDoc}
                    onSave={onApply}
                    existingPartCount={predicateParts?.length}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : undefined}
                />
            )}
            {deletingPart ? (
                <ConfirmationModal
                    header={`Delete Predicate Part?`}
                    body={`Are you sure you want to delete ${CriteriaHelper.getPartLabel(deletingPart)}?`}
                    confirmButtonText={'Yes, Delete'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setDeleting(null)}
                    onConfirm={() => {
                        return new Promise((resolve) => {
                            onDelete(deletingPart);
                            resolve();
                        });
                    }}
                />
            ) : null}
        </>
    );
};

PredicatePartsTable.displayName = 'PredicatePartsTable';

export default PredicatePartsTable;

PredicatePartsTable.propTypes = {
    parentPredicateUuid: PropTypes.string,
    predicatePartUuids: PropTypes.arrayOf(PropTypes.string),
    setEdited: PropTypes.func,
    onApply: PropTypes.func,
    onChangeSortOrder: PropTypes.func,
    onDelete: PropTypes.func,
    parentDrawerRef: PropTypes.any
};
